import dayjs from "dayjs";
import { t } from "@/plugins/i18n";
import { ResourceTreeCategory } from "@/ui/components/FileBrowser/types";
import FileDTO = App.Data.FileDTO;

export type FileColumnContext = {
	locationOf: (file: FileDTO) => string;
};

const EM_DASH = "\u2014";

const formatDate = (value: string | null): string =>
	value ? dayjs(value).format("DD MMM YYYY HH:mm") : EM_DASH;

export type FileSortKey =
	| "title"
	| "last_modified_sort"
	| "times_opened"
	| "created_at"
	| "last_opened_at"
	| "tags";

export type FileColumn = {
	key: string;
	sortKey?: FileSortKey;
	align?: "start" | "end";
	width?: string;
	only?: ResourceTreeCategory;
	globalOnly?: boolean;
	value?: (file: FileDTO, context: FileColumnContext) => string | number;
};

/**
 * The column's header, translated when it is rendered rather than when this
 * module loads: the messages are merged into i18n during app start-up, which
 * an import-time lookup would race.
 */
export const columnLabel = (column: FileColumn): string =>
	t(`fileBrowser.columns.${column.key}`);

export const FILE_COLUMNS: FileColumn[] = [
	{ key: "title", sortKey: "title" },
	{
		key: "tags",
		sortKey: "tags",
		width: "160px",
	},
	{
		key: "created_at",
		sortKey: "created_at",
		width: "150px",
		value: (file) => formatDate(file.created_at),
	},
	{
		key: "last_modified_at",
		sortKey: "last_modified_sort",
		width: "150px",
		value: (file) => formatDate(file.last_modified_at),
	},
	{
		key: "last_modified_by",
		width: "150px",
		value: (file) => file.last_modified_by?.name ?? EM_DASH,
	},
	{
		key: "user_id",
		width: "150px",
		value: (file) => file.user?.name ?? EM_DASH,
	},
	{
		key: "mode",
		only: "dashboards",
		width: "150px",
		value: (file) =>
			file.mode ? t(`fileBrowser.modes.${file.mode}`) : EM_DASH,
	},
	{
		key: "view_tab",
		only: "requested_documents",
		width: "150px",
		value: (file) =>
			file.view_tab ? t(`fileBrowser.viewTabs.${file.view_tab}`) : EM_DASH,
	},
	{
		key: "last_opened_at",
		sortKey: "last_opened_at",
		width: "150px",
		value: (file) => formatDate(file.last_opened_at),
	},
	{
		key: "times_opened",
		sortKey: "times_opened",
		align: "end",
		width: "120px",
		value: (file) => file.times_opened ?? 0,
	},
	{
		key: "count_users_with_abilities",
		align: "end",
		width: "110px",
		value: (file) => file.count_users_with_abilities || EM_DASH,
	},
	{
		key: "location",
		width: "180px",
		globalOnly: true,
		value: (file, { locationOf }) => locationOf(file),
	},
];

export const allColumnKeys = (): string[] => {
	return FILE_COLUMNS.map((column) => column.key);
};

export const eligibleColumns = (
	category: ResourceTreeCategory,
	isSearchingEverywhere: boolean
): FileColumn[] =>
	FILE_COLUMNS.filter(
		(column) =>
			(!column.only || column.only === category) &&
			(!column.globalOnly || isSearchingEverywhere)
	);

export const ROW_HEIGHT = 24;
